class CartNew extends HTMLElement {
  constructor() {
    super();
    this.processing = new Set();
    // Track previous discount state to detect changes
    this.previousDiscountState = undefined;
    // Monotonic stamp for cart mutations: every pipeline takes a new stamp at
    // dispatch; only the newest stamp may render, and the render itself always
    // comes from a fresh /cart.js fetch after the last in-flight pipeline ends
    this.mutationSeq = 0;
    this.inflightMutations = 0;
    this.hadPipelineFailure = false;
    // S8: set by mutation pipelines (child-quantity cascade, line removal),
    // spoken with the totals in the settle announcement
    this.pendingSyncNote = '';
    // set by keyboard-driven removals; consumed by the settle pass to restore
    // focus after the re-render destroys the focused control
    this.pendingFocusKeys = null;
    this.init();
  }

  init() {
    this.bindEvents();
    this.setupCartChangeListener();
    // Initialize discount state on page load
    this.initializeDiscountState();
  }

  async initializeDiscountState() {
    try {
      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) {
        const cartData = await cartResponse.json();
        const hasDiscounts = cartData.items && cartData.items.some(item => 
          item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0
        ) || (cartData.cart_level_discount_applications && cartData.cart_level_discount_applications.length > 0);
        this.previousDiscountState = hasDiscounts;
      }
    } catch (error) {
      console.warn('Failed to initialize discount state:', error);
      this.previousDiscountState = false;
    }
  }

  bindEvents() {
    // Quantity buttons
    this.addEventListener('click', (e) => {
      const checkoutBtn = e.target.closest('.cart-new__checkout-btn');
      if (checkoutBtn && checkoutBtn.getAttribute('aria-disabled') === 'true') {
        // a mutation pipeline is still in flight — don't let the shopper
        // navigate to checkout with the cart in an unsettled state
        e.preventDefault();
        return;
      }

      const decreaseBtn = e.target.closest('.cart-new__quantity-btn--decrease');
      const increaseBtn = e.target.closest('.cart-new__quantity-btn--increase');
      const removeBtn = e.target.closest('.cart-new__remove-btn');

      if (decreaseBtn) {
        e.preventDefault();
        this.handleQuantityChange(decreaseBtn.dataset.lineKey, 'decrease');
      } else if (increaseBtn) {
        e.preventDefault();
        this.handleQuantityChange(increaseBtn.dataset.lineKey, 'increase');
      } else if (removeBtn) {
        e.preventDefault();
        this.handleRemoveItem(removeBtn.dataset.lineKey);
      } else {
        // S7: the pill's dead space must forward taps to the small input
        // instead of swallowing them
        const pill = e.target.closest('.cart-new__quantity-controls');
        if (pill && e.target.tagName !== 'INPUT') {
          const input = pill.querySelector('.cart-new__quantity-input');
          if (input) {
            input.focus();
            input.select();
          }
        }
      }
    });

    // Quantity input changes — read the input that actually fired, and treat
    // what it displays as intent: garbage restores state, 0 removes the line
    this.addEventListener('change', (e) => {
      if (!e.target.classList.contains('cart-new__quantity-input')) return;
      const input = e.target;
      const lineKey = input.dataset.lineKey;
      const typed = parseInt(input.value, 10);
      if (isNaN(typed)) {
        this.restoreLineQuantityDisplays(lineKey);
        return;
      }
      if (typed <= 0) {
        this.handleRemoveItem(lineKey);
        return;
      }
      this.handleQuantityChange(lineKey, 'set', typed);
    });
  }

  // The value ATTRIBUTE (not the live property) doubles as the per-line state
  // marker: server-rendered HTML seeds it, and every JS write keeps it in sync
  getLineStateQuantity(itemRow) {
    const input = itemRow.querySelector('.cart-new__quantity-input');
    if (!input) return 1;
    const stateValue = parseInt(input.getAttribute('value'), 10);
    return isNaN(stateValue) ? 1 : stateValue;
  }

  setLineQuantityDisplays(itemRow, quantity) {
    itemRow.querySelectorAll('.cart-new__quantity-input').forEach(inp => {
      inp.value = quantity;
      inp.setAttribute('value', quantity);
    });
  }

  restoreLineQuantityDisplays(lineKey) {
    const itemRow = this.querySelector(`[data-line-key="${lineKey}"]`);
    if (!itemRow) return;
    this.setLineQuantityDisplays(itemRow, this.getLineStateQuantity(itemRow));
  }

  getChildLineKeys(lineKey) {
    // attached upsell rows carry data-parent-key pointing at their parent row
    return Array.from(this.querySelectorAll(`tr[data-parent-key="${lineKey}"]`))
      .map(row => row.getAttribute('data-line-key'))
      .filter(Boolean);
  }

  syncChildGroupClasses() {
    // keep the visual grouping classes correct after rows are added/removed
    const rows = this.querySelectorAll('tr[data-line-key]');
    rows.forEach(row => {
      if (row.classList.contains('cart-new__item--child')) return;
      const key = row.getAttribute('data-line-key');
      const children = this.querySelectorAll(`tr[data-parent-key="${key}"]`);
      row.classList.toggle('cart-new__item--has-children', children.length > 0);
      children.forEach((child, index) => {
        child.classList.toggle('cart-new__item--child-last', index === children.length - 1);
      });
    });
  }

  setupCartChangeListener() {
    // Listen for cart changes from other components
    this.cartChangeHandler = async (event) => {
      if (event.detail && event.detail.cart) {
        // Don't update if we're already processing a change
        if (this.processing.size > 0) {
          return;
        }
        
        // If discount was applied or removed, reload page to show badges and prices correctly
        if (event.detail.baseEvent === 'discount:applied' || event.detail.baseEvent === 'discount:removed') {
          window.location.reload();
          return;
        } else {
          await this.updateCartDisplay(event.detail.cart);
        }
      }
    };
    
    document.addEventListener('cart:change', this.cartChangeHandler);

    // Also listen for cart:updated events
    this.cartUpdatedHandler = async (event) => {
      if (event.detail && event.detail.cart) {
        // Don't update if we're already processing a change
        if (this.processing.size > 0) {
          return;
        }
        
        // Check if this update includes discounts by looking at line items
        const hasDiscounts = event.detail.cart.items && event.detail.cart.items.some(item => 
          item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0
        ) || (event.detail.cart.cart_level_discount_applications && event.detail.cart.cart_level_discount_applications.length > 0);
        
        // Check if discounts status changed (applied or removed)
        // Store previous discount state if not exists
        if (this.previousDiscountState === undefined) {
          this.previousDiscountState = hasDiscounts;
        }
        
        // If discount status changed (applied or removed), reload page
        if (this.previousDiscountState !== hasDiscounts) {
          this.previousDiscountState = hasDiscounts;
          window.location.reload();
          return;
        }
        
        // Otherwise, update normally
        await this.updateCartDisplay(event.detail.cart);
      }
    };
    
    document.addEventListener('cart:updated', this.cartUpdatedHandler);
  }

  async handleQuantityChange(lineKey, action, quantity = null) {
    // Validate lineKey
    if (!lineKey || typeof lineKey !== 'string' || lineKey.trim() === '') {
      console.error('Invalid lineKey:', lineKey);
      return;
    }

    if (this.processing.has(lineKey)) {
      console.warn('Already processing item:', lineKey);
      // never leave a typed value on screen that was not applied
      this.restoreLineQuantityDisplays(lineKey);
      return;
    }

    const itemRow = this.querySelector(`[data-line-key="${lineKey}"]`);
    if (!itemRow) {
      console.error('Item row not found for lineKey:', lineKey);
      return;
    }

    const allInputs = itemRow.querySelectorAll('.cart-new__quantity-input');
    if (allInputs.length === 0) {
      console.error('No quantity inputs found for item:', lineKey);
      return;
    }

    const currentQuantity = this.getLineStateQuantity(itemRow);
    const maxQuantity = allInputs[0].max ? parseInt(allInputs[0].max, 10) : null;
    let newQuantity = currentQuantity;

    if (action === 'decrease') {
      newQuantity = Math.max(1, currentQuantity - 1);
    } else if (action === 'increase') {
      newQuantity = maxQuantity ? Math.min(maxQuantity, currentQuantity + 1) : currentQuantity + 1;
    } else if (action === 'set' && quantity !== null) {
      newQuantity = Math.max(1, quantity);
      if (maxQuantity) {
        newQuantity = Math.min(maxQuantity, newQuantity);
      }
    }

    // Write the resolved value into every input for this line before deciding
    // whether to hit the server — the input that fired the event may display
    // something else (a clamped 0, "007", surrounding whitespace)
    this.setLineQuantityDisplays(itemRow, newQuantity);
    if (newQuantity === currentQuantity) return;

    // attached children mirror the parent quantity
    const childKeys = this.getChildLineKeys(lineKey);
    const childRows = childKeys.map(key => this.querySelector(`[data-line-key="${key}"]`)).filter(Boolean);

    if (childRows.length > 0) {
      // S8: name the synced children in the settle announcement. First text
      // node only — the title span also carries a visually hidden
      // "(included with …)" suffix that shouldn't be spoken twice
      const childNames = childRows.map(row => {
        const title = row.querySelector('.cart-new__product-title');
        const text = title && title.childNodes[0] ? title.childNodes[0].textContent.trim() : '';
        return text || 'included item';
      });
      this.queueSyncNote(childNames.join(', ') + ' quantity matched to ' + newQuantity + '.');

      // Optimistic child write, mirroring the parent's input write above:
      // quantity, mobile receipt and line total update the moment the stepper
      // is tapped, not a round-trip later when the child's own sync request
      // dispatches. The settle render reconciles all of it to server truth —
      // including on failed/partial sequences, where renderFromTruth runs
      // before the error banner is shown.
      childRows.forEach(row => {
        row.querySelectorAll('.cart-new__child-qty').forEach(el => {
          el.textContent = newQuantity;
          el.hidden = newQuantity < 2;
        });
        const unitCents = parseInt(row.getAttribute('data-unit-price'), 10);
        if (!isNaN(unitCents)) {
          row.querySelectorAll('.cart-new__child-price--mobile').forEach(el => {
            el.textContent = newQuantity > 1
              ? newQuantity + ' × ' + this.formatMoney(unitCents)
              : this.formatMoney(unitCents);
          });
          const lineTotalEl = row.querySelector('.cart-new__line-total-price');
          if (lineTotalEl) {
            lineTotalEl.textContent = this.formatMoney(unitCents * newQuantity);
          }
        }
      });
    }

    this.processing.add(lineKey);
    childKeys.forEach(key => this.processing.add(key));
    itemRow.setAttribute('data-loading', 'true');
    childRows.forEach(row => row.setAttribute('data-loading', 'true'));

    this.beginMutation();
    let failed = false;
    try {
      // parent first, then each attached child, all set to the same quantity.
      // A 400/404 means the line no longer exists server-side (removed in
      // another tab, or an earlier step of this cascade) — skip it and keep
      // going; the settle render at the end reconciles the display
      const keysToSync = [lineKey].concat(childKeys);
      for (const key of keysToSync) {
        try {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: key,
              quantity: newQuantity
            })
          });
          if (!response.ok) {
            if (response.status === 400 || response.status === 404) {
              console.warn('Line already removed, skipping quantity sync:', key);
            } else {
              failed = true;
              let errorData;
              try {
                errorData = await response.json();
              } catch (e) {
                errorData = { status: response.status };
              }
              console.error('Error updating quantity for', key, errorData);
            }
          }
        } catch (requestError) {
          failed = true;
          console.error('Network error updating quantity for', key, requestError);
        }
      }
    } finally {
      this.processing.delete(lineKey);
      childKeys.forEach(key => this.processing.delete(key));
      itemRow.removeAttribute('data-loading');
      childRows.forEach(row => row.removeAttribute('data-loading'));
      await this.endMutation(failed);
    }
  }

  async handleRemoveItem(lineKey) {
    if (this.processing.has(lineKey)) return;

    const itemRow = this.querySelector(`[data-line-key="${lineKey}"]`);
    if (!itemRow) return;

    // removing a parent removes its attached children too — children first,
    // so an interrupted sequence never leaves an orphaned child behind
    const childKeys = this.getChildLineKeys(lineKey);
    const childRows = childKeys.map(key => this.querySelector(`[data-line-key="${key}"]`)).filter(Boolean);
    const keysToRemove = childKeys.concat([lineKey]);

    // name the removed line in the settle announcement. First text node only —
    // same rule as the child-sync note: the title span carries a visually
    // hidden suffix that shouldn't be spoken
    const titleEl = itemRow.querySelector('.cart-new__product-title');
    const removedName = (titleEl && titleEl.childNodes[0] ? titleEl.childNodes[0].textContent.trim() : '') || 'Item';
    this.queueSyncNote(removedName + ' removed from cart.');

    // restore focus later only for keyboard-driven removals — the trigger
    // (remove button, or the quantity input a 0 was typed into) is focused and
    // :focus-visible. After a touch/mouse removal focus falls to body today
    // without harm, and programmatically focusing an input would pop the
    // on-screen keyboard on touch devices.
    let keyboardDriven = false;
    const trigger = document.activeElement;
    if (trigger && itemRow.contains(trigger)) {
      try {
        keyboardDriven = trigger.matches(':focus-visible');
      } catch (e) {
        keyboardDriven = true;
      }
    }

    // where focus should land once the settle re-render destroys this row
    // (and any attached children): the next surviving line, else the previous
    // one. Line keys are stable across the re-render, DOM nodes are not. The
    // removed line's own key goes first — if the removal fails and the row
    // survives, focus belongs back on it, not on a neighbor.
    const removedKeySet = new Set(keysToRemove);
    const allRows = Array.from(this.querySelectorAll('tr[data-line-key]'));
    const rowIndex = allRows.indexOf(itemRow);
    let adjacentKey = null;
    for (let i = rowIndex + 1; i < allRows.length && !adjacentKey; i++) {
      const key = allRows[i].getAttribute('data-line-key');
      if (!removedKeySet.has(key)) adjacentKey = key;
    }
    for (let i = rowIndex - 1; i >= 0 && !adjacentKey; i--) {
      const key = allRows[i].getAttribute('data-line-key');
      if (!removedKeySet.has(key)) adjacentKey = key;
    }
    this.pendingFocusKeys = keyboardDriven ? [lineKey, adjacentKey] : null;

    this.processing.add(lineKey);
    childKeys.forEach(key => this.processing.add(key));
    itemRow.setAttribute('data-loading', 'true');
    childRows.forEach(row => row.setAttribute('data-loading', 'true'));

    this.beginMutation();
    let failed = false;
    try {
      for (const key of keysToRemove) {
        try {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: key,
              quantity: 0
            })
          });
          if (!response.ok) {
            if (response.status === 400 || response.status === 404) {
              // line is already gone — exactly what removal wanted anyway
              console.warn('Line already removed, skipping:', key);
            } else {
              failed = true;
              let errorData;
              try {
                errorData = await response.json();
              } catch (e) {
                errorData = { status: response.status };
              }
              console.error('Error removing item', key, errorData);
            }
          }
        } catch (requestError) {
          failed = true;
          console.error('Network error removing item', key, requestError);
        }
      }
    } finally {
      this.processing.delete(lineKey);
      childKeys.forEach(key => this.processing.delete(key));
      itemRow.removeAttribute('data-loading');
      childRows.forEach(row => row.removeAttribute('data-loading'));
      await this.endMutation(failed);
    }
  }

  // After a removal settles, the focused control is gone (row.remove() or the
  // wholesale tbody swap) and focus has fallen to <body>, dumping keyboard
  // users at the top of the document. Called by the settle pass after the
  // re-render and BEFORE the announcement — a focus move interrupts pending
  // polite speech, so it must land first for the removal message to be heard.
  // Never steals focus the shopper already moved to something that survived.
  restorePendingFocus() {
    const keys = this.pendingFocusKeys;
    this.pendingFocusKeys = null;
    if (!keys) return;
    const active = document.activeElement;
    if (active && active !== document.body && active !== document.documentElement) return;
    for (const key of keys) {
      if (!key) continue;
      const row = this.querySelector(`tr[data-line-key="${key}"]`);
      if (!row) continue;
      // prefer the quantity input: landing on a remove button invites a
      // second Enter deleting a second line. Child rows have no quantity
      // input, so fall back to their remove button. Each row carries mobile
      // and desktop variants of both controls — pick the rendered one.
      const target =
        Array.from(row.querySelectorAll('.cart-new__quantity-input')).find(el => el.getClientRects().length > 0) ||
        Array.from(row.querySelectorAll('.cart-new__remove-btn')).find(el => el.getClientRects().length > 0);
      if (target) {
        target.focus();
        return;
      }
    }
  }

  // S8: notes accumulate rather than overwrite — overlapping pipelines (a
  // child-quantity cascade still in flight when a removal dispatches) share
  // one settle announcement, and every event should be spoken in it
  queueSyncNote(note) {
    this.pendingSyncNote = this.pendingSyncNote ? this.pendingSyncNote + ' ' + note : note;
  }

  beginMutation() {
    this.mutationSeq += 1;
    this.inflightMutations += 1;
    this.setCheckoutDisabled(true);
    this.clearError();
    return this.mutationSeq;
  }

  // Called once per pipeline. Only the last pipeline standing settles: it
  // re-fetches /cart.js and renders from that truth, so a slow earlier
  // response can never repaint over a newer state
  async endMutation(failed) {
    if (failed) this.hadPipelineFailure = true;
    this.inflightMutations -= 1;
    if (this.inflightMutations > 0) return;

    const settleSeq = this.mutationSeq;
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error(`Failed to fetch cart state: ${response.status}`);
      }
      const cartData = await response.json();
      // stale guard: a newer mutation was dispatched while we were fetching —
      // its own settle pass will render
      if (settleSeq !== this.mutationSeq || this.inflightMutations > 0) return;

      if (cartData.item_count === 0) {
        window.location.reload();
        return;
      }

      await this.renderFromTruth(cartData);
      // focus first, then speak: writing the live region before moving focus
      // would get the message cancelled by the focus announcement
      this.restorePendingFocus();
      if (this.hadPipelineFailure) {
        this.showError("Some items couldn't be updated. Your cart has been refreshed — please review it before checking out.");
      } else {
        // S8: polite announcement of the sync + fresh totals (the error banner
        // above is role=alert and announces itself)
        const note = this.pendingSyncNote ? this.pendingSyncNote + ' ' : '';
        this.announce(note + 'Cart total ' + this.formatMoney(cartData.total_price) + '.');
      }
      this.pendingSyncNote = '';
    } catch (error) {
      console.error('Error settling cart state:', error);
      if (settleSeq === this.mutationSeq && this.inflightMutations === 0) {
        // no re-render happened, so the focused control usually survived and
        // restorePendingFocus no-ops — called to clear the pending keys
        this.restorePendingFocus();
        this.showError("We couldn't update your cart. Please refresh the page and try again.");
      }
    } finally {
      if (settleSeq === this.mutationSeq && this.inflightMutations === 0) {
        this.hadPipelineFailure = false;
        this.setCheckoutDisabled(false);
      }
    }
  }

  async renderFromTruth(cartData) {
    // Temporarily remove listeners so the cart:change we dispatch below
    // doesn't re-render on top of itself
    if (this.cartChangeHandler) {
      document.removeEventListener('cart:change', this.cartChangeHandler);
    }
    if (this.cartUpdatedHandler) {
      document.removeEventListener('cart:updated', this.cartUpdatedHandler);
    }
    try {
      await this.updateCartDisplay(cartData);
    } finally {
      setTimeout(() => {
        if (this.cartChangeHandler) {
          document.addEventListener('cart:change', this.cartChangeHandler);
        }
        if (this.cartUpdatedHandler) {
          document.addEventListener('cart:updated', this.cartUpdatedHandler);
        }
      }, 100);
    }

    // Trigger cart change event for other components (but not for ourselves)
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { cart: cartData }
    }));
  }

  // Visually hidden polite live region (S8). Server-rendered empty in
  // main-cart-new.liquid so it exists at page load — a region created and
  // filled in the same synchronous block loses its first message in
  // NVDA/JAWS. Lives on the section element, outside the tbody/totals that
  // re-renders replace, so it survives them. The create branch is only a
  // fallback for markup drift.
  ensureStatusRegion() {
    let region = this.querySelector('.cart-new__sr-status');
    if (!region) {
      region = document.createElement('div');
      region.className = 'sr-only cart-new__sr-status';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      this.appendChild(region);
    }
    return region;
  }

  announce(message) {
    const region = this.ensureStatusRegion();
    // alternate a trailing nbsp so an identical repeat still re-announces
    this._announceTick = !this._announceTick;
    region.textContent = message + (this._announceTick ? ' ' : '');
  }

  setCheckoutDisabled(disabled) {
    this.querySelectorAll('.cart-new__checkout-btn').forEach(btn => {
      btn.classList.toggle('cart-new__checkout-btn--disabled', disabled);
      if (disabled) {
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.removeAttribute('aria-disabled');
      }
    });
  }

  showError(message) {
    let banner = this.querySelector('.cart-new__error-banner');
    if (!banner) {
      banner = document.createElement('div');
      banner.className = 'cart-new__error-banner';
      banner.setAttribute('role', 'alert');
      const main = this.querySelector('.cart-new__main');
      const tableWrapper = this.querySelector('.cart-new__table-wrapper');
      if (main && tableWrapper) {
        main.insertBefore(banner, tableWrapper);
      } else {
        this.prepend(banner);
      }
    }
    banner.textContent = message;
    banner.hidden = false;
  }

  clearError() {
    const banner = this.querySelector('.cart-new__error-banner');
    if (banner) banner.hidden = true;
  }

  async updateCartDisplay(cartData) {
    // If cart is empty, reload page
    if (!cartData || cartData.item_count === 0) {
      window.location.reload();
      return;
    }

    // Try manual update first
    try {
      this.updateCartItems(cartData.items);
      this.updateCartTotals(cartData);
      this.updateFreeShipping(cartData);
      this.updateAffirmMessaging(cartData.total_price);
    } catch (error) {
      // Exact line-key matching failed (row added/removed outside this view,
      // DOM shape changed) — re-render the whole section from the server
      // instead of guessing which row is which
      console.warn('Manual update failed, re-rendering section:', error.message);
      try {
        await this.refreshCartSection();
        this.updateCartTotals(cartData);
        this.updateAffirmMessaging(cartData.total_price);
      } catch (refreshError) {
        console.error('Section re-render failed:', refreshError);
        // Last resort - reload page
        window.location.reload();
      }
    }
  }

  // Re-render the section from the server. Throws on any failure — callers
  // decide the fallback (usually a page reload); a failed refresh must never
  // masquerade as success
  async refreshCartSection() {
    // The Section Rendering API resolves JSON-template sections only by their
    // FULL dynamic id (e.g. "template--123__main_cart_new_LP3zV6"). Asking for
    // a normalized name like "main-cart-new" renders the section file with
    // DEFAULT schema settings instead of this template's configured ones
    const sectionElement = this.closest('[id^="shopify-section-"]');
    if (!sectionElement) {
      throw new Error('Cart section wrapper element not found');
    }
    const sectionId = sectionElement.id.replace('shopify-section-', '');

    const response = await fetch(`/cart?sections=${encodeURIComponent(sectionId)}`, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch section: ${response.status}`);
    }

    // GET /cart?sections= responds with a flat {"<section-id>": "<html>"}
    // object — there is no "sections" wrapper
    const data = await response.json();
    const newSectionHTML = data[sectionId];
    if (!newSectionHTML) {
      throw new Error(`Section render missing or null for id: ${sectionId}`);
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(newSectionHTML, 'text/html');
    const newCartSection = doc.querySelector('cart-new-section') || doc.querySelector('.cart-new');
    if (!newCartSection) {
      // the section rendered its empty-cart state — no table to graft in
      window.location.reload();
      return;
    }

    // Update items table
    const newTbody = newCartSection.querySelector('#cart-new-items');
    const currentTbody = this.querySelector('#cart-new-items');
    if (!newTbody || !currentTbody) {
      throw new Error('Items table not found in new or current section');
    }

    // The fresh server HTML already carries correct line keys on every row,
    // input and button — replace wholesale, never re-key rows by position
    currentTbody.innerHTML = newTbody.innerHTML;

    // Update sidebar totals
    const newSidebar = newCartSection.querySelector('.cart-new__sidebar');
    const currentSidebar = this.querySelector('.cart-new__sidebar');

    if (newSidebar && currentSidebar) {
      // Update totals
      const newTotals = newSidebar.querySelector('.cart-new__totals');
      const currentTotals = currentSidebar.querySelector('.cart-new__totals');
      if (newTotals && currentTotals) {
        currentTotals.innerHTML = newTotals.innerHTML;
      }

      // Update free shipping
      const newFreeShipping = newSidebar.querySelector('#cart-new-free-shipping');
      const currentFreeShipping = currentSidebar.querySelector('#cart-new-free-shipping');
      if (newFreeShipping && currentFreeShipping) {
        currentFreeShipping.innerHTML = newFreeShipping.innerHTML;
      }

      // Update discount component
      const newDiscount = newSidebar.querySelector('.cart-new__discount');
      const currentDiscount = currentSidebar.querySelector('.cart-new__discount');
      if (newDiscount && currentDiscount) {
        currentDiscount.innerHTML = newDiscount.innerHTML;
      }
    }

    // Reinitialize cart discount component if needed
    const discountComponent = this.querySelector('cart-discount-component');
    if (discountComponent && typeof discountComponent.init === 'function') {
      discountComponent.init();
    }

    // Format all prices to remove trailing zeros
    this.formatAllPrices();
  }

  formatAllPrices() {
    // Find all price elements and remove all decimal parts
    const priceSelectors = [
      '.cart-new__price-regular',
      '.cart-new__price-sale',
      '.cart-new__price-compare',
      '.cart-new__line-total-price',
      '#cart-new-subtotal',
      '#cart-new-discount',
      '#cart-new-total'
    ];

    priceSelectors.forEach(selector => {
      const elements = this.querySelectorAll(selector);
      elements.forEach(el => {
        if (el.textContent) {
          let text = el.textContent.trim();
          // Remove all decimal parts (everything after the dot including the dot itself)
          // Match pattern: digit(s) followed by dot and any digits, then optional space/currency/end
          text = text.replace(/(\d+)\.\d+/g, '$1');
          el.textContent = text;
        }
      });
    });
  }

  updateCartItems(items) {
    if (!items || items.length === 0) {
      // If no items, check if cart is really empty or just temporary state
      // Use Sections API to get accurate state
      this.refreshCartSection().catch(() => {
        // If refresh fails and cart is really empty, reload
        window.location.reload();
      });
      return;
    }

    // First, ensure all items exist in the DOM
    const tbody = this.querySelector('#cart-new-items');
    if (!tbody) return;

    const existingLineKeys = new Set();
    const itemsToUpdate = new Map();
    
    // Collect all items that need to be updated
    items.forEach(item => {
      itemsToUpdate.set(item.key, item);
      existingLineKeys.add(item.key);
    });

    const allExistingRows = Array.from(tbody.querySelectorAll('tr.cart-new__item, .cart-new__item'));

    // Update existing items — rows are matched by exact line key ONLY. If a
    // key has no row, this DOM is stale; throw so the caller re-renders the
    // whole section from the server. Never map rows to items by position
    items.forEach((item) => {
      if (!item || !item.key) {
        console.warn('Invalid item in cart data:', item);
        return;
      }

      const itemRow = this.querySelector(`tr[data-line-key="${item.key}"]`) ||
                      this.querySelector(`.cart-new__item[data-line-key="${item.key}"]`);

      if (!itemRow) {
        console.warn(`Item ${item.key} not found in DOM. Items: ${items.length}, Rows: ${allExistingRows.length}`);
        throw new Error(`Item ${item.key} not found - need full refresh`);
      }

      // Update quantity - update ALL inputs for this item (desktop and mobile);
      // the value attribute doubles as the state marker for typed-input handling
      const allInputs = itemRow.querySelectorAll('.cart-new__quantity-input');
      allInputs.forEach(input => {
        input.value = item.quantity;
        input.setAttribute('value', item.quantity);
      });

      // Update buttons state - update ALL buttons for this item (desktop and mobile)
      const allDecreaseBtns = itemRow.querySelectorAll('.cart-new__quantity-btn--decrease');
      const allIncreaseBtns = itemRow.querySelectorAll('.cart-new__quantity-btn--increase');
      
      allDecreaseBtns.forEach(decreaseBtn => {
        decreaseBtn.disabled = item.quantity <= 1;
        decreaseBtn.setAttribute('data-line-key', item.key);
      });
      
      allIncreaseBtns.forEach(increaseBtn => {
        const input = itemRow.querySelector('.cart-new__quantity-input');
        if (input && input.max) {
          increaseBtn.disabled = item.quantity >= parseInt(input.max);
        } else {
          increaseBtn.disabled = false;
        }
        increaseBtn.setAttribute('data-line-key', item.key);
      });

      // Attached child rows show a static bare number instead of a stepper —
      // the column header already reads "Quantity"
      itemRow.querySelectorAll('.cart-new__child-qty').forEach(el => {
        el.textContent = item.quantity;
        el.hidden = item.quantity < 2;
      });
      // mobile child uses the receipt convention "2 × $49" (S9) so the real
      // charge stays legible; desktop keeps line totals in the Total column
      itemRow.querySelectorAll('.cart-new__child-price--mobile').forEach(el => {
        el.textContent = item.quantity > 1
          ? item.quantity + ' × ' + this.formatMoney(item.final_price)
          : this.formatMoney(item.final_price);
      });

      // Update remove buttons - a row has one per layout (desktop and mobile),
      // and every one of them must carry the current key
      itemRow.querySelectorAll('.cart-new__remove-btn').forEach(removeBtn => {
        removeBtn.setAttribute('data-line-key', item.key);
      });

      // Update line total
      const lineTotalEl = itemRow.querySelector('.cart-new__line-total');
      if (lineTotalEl) {
        // Remove existing price elements
        const existingPrice = lineTotalEl.querySelector('.cart-new__line-total-price');
        const existingComparePrice = lineTotalEl.querySelector('.cart-new__line-total-price-compare');
        
        if (existingPrice) existingPrice.remove();
        if (existingComparePrice) existingComparePrice.remove();
        
        // Always show regular price without compare price
        const regularPrice = document.createElement('span');
        regularPrice.className = 'cart-new__line-total-price';
        regularPrice.textContent = this.formatMoney(item.final_line_price);
        lineTotalEl.appendChild(regularPrice);
      }

      // Update product price - handle discounts and compare price
      const priceContainer = itemRow.querySelector('.cart-new__product-price');
      if (!priceContainer) return;
      
      const priceSale = itemRow.querySelector('.cart-new__price-sale');
      const priceRegular = itemRow.querySelector('.cart-new__price-regular');
      const priceCompare = itemRow.querySelector('.cart-new__price-compare');
      let discountBadge = itemRow.querySelector('.cart-new__discount-badge');
      
      // Get compare price from existing DOM element or data attribute
      // This preserves compare_at_price which might not be in cart.js response
      let savedComparePrice = null;
      
      // First, check if item row has compare price stored in data attribute
      const comparePriceFromRow = itemRow.getAttribute('data-compare-price');
      if (comparePriceFromRow) {
        savedComparePrice = parseInt(comparePriceFromRow);
      }
      
      // Also check compare price element data attribute
      if (!savedComparePrice && priceCompare) {
        const comparePriceData = priceCompare.getAttribute('data-compare-price') || priceCompare.getAttribute('data-price');
        if (comparePriceData) {
          savedComparePrice = parseInt(comparePriceData);
        }
      }
      
      // Check if item has line level discounts
      const hasDiscount = item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0;
      const originalPrice = item.original_price;
      const finalPrice = item.final_price;
      
      // Check if variant has compare_at_price in API response
      const hasComparePriceInAPI = item.variant && item.variant.compare_at_price && 
                              item.variant.compare_at_price > finalPrice;
      
      // Use compare_at_price from API if available, otherwise use saved from DOM, or original_price for discounts
      let comparePriceValue = null;
      if (hasComparePriceInAPI) {
        comparePriceValue = item.variant.compare_at_price;
      } else if (savedComparePrice && savedComparePrice > finalPrice) {
        comparePriceValue = savedComparePrice;
      } else if (hasDiscount || originalPrice > finalPrice) {
        comparePriceValue = originalPrice;
      }
      
      // Check if compare price should be shown based on section setting
      const showComparePriceSetting = this.getAttribute('data-show-compare-price');
      const isComparePriceEnabled = showComparePriceSetting === null || showComparePriceSetting === '' || showComparePriceSetting === 'true';
      
      // Show compare price only if setting is enabled
      const shouldShowComparePrice = isComparePriceEnabled && ((hasComparePriceInAPI || (savedComparePrice && savedComparePrice > finalPrice)) || (hasDiscount && originalPrice > finalPrice));
      
      // If compare price setting is disabled, always show regular price (even with discount or compare price in data)
      if (!isComparePriceEnabled) {
        // Hide sale price and compare price when setting is disabled
        if (priceSale) {
          priceSale.style.display = 'none';
        }
        if (priceCompare) {
          priceCompare.style.display = 'none';
        }
        
        // Show regular price (ordinary color, not red)
        if (!priceRegular) {
          const regularPrice = document.createElement('span');
          regularPrice.className = 'cart-new__price-regular';
          regularPrice.textContent = this.formatMoney(finalPrice);
          if (priceSale && priceSale.parentNode) {
            priceContainer.insertBefore(regularPrice, priceSale);
          } else if (priceCompare && priceCompare.parentNode) {
            priceContainer.insertBefore(regularPrice, priceCompare);
          } else {
            priceContainer.appendChild(regularPrice);
          }
        } else {
          priceRegular.textContent = this.formatMoney(finalPrice);
          priceRegular.style.display = 'inline';
        }
        
        // Still show discount badges even if compare price is disabled
        if (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) {
          const discountText = item.line_level_discount_allocations
            .map(d => `${d.discount_application.title} (-${this.formatMoney(d.amount)})`)
            .join(', ');
          
          let discountBadge = itemRow.querySelector('.cart-new__discount-badge');
          if (!discountBadge && priceContainer) {
            discountBadge = document.createElement('span');
            discountBadge.className = 'cart-new__discount-badge';
            discountBadge.innerHTML = `
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                <g clip-path="url(#clip0_2486_1092)">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M4.06792 0.313109C4.35156 0.303328 4.62653 0.411715 4.82722 0.6124L9.38759 5.17278C9.78747 5.57264 9.78747 6.22095 9.38759 6.62083L6.62083 9.38761C6.22095 9.78747 5.57264 9.78747 5.17278 9.38759L0.6124 4.82722C0.411715 4.62653 0.303328 4.35156 0.313109 4.06791L0.405336 1.39336C0.423879 0.855556 0.855559 0.423879 1.39336 0.405334L4.06792 0.313109ZM4.38528 1.05434C4.30709 0.976154 4.19997 0.933926 4.08945 0.937737L1.4149 1.02996C1.20537 1.03719 1.03719 1.20537 1.02996 1.4149L0.937737 4.08945C0.933926 4.19995 0.976154 4.30709 1.05434 4.38528L5.61472 8.94566C5.77051 9.10145 6.02309 9.10145 6.17887 8.94566L8.94566 6.17887C9.10145 6.02309 9.10145 5.77051 8.94566 5.61472L4.38528 1.05434ZM2.28023 2.28023C2.61064 1.94983 3.14634 1.94983 3.47675 2.28023C3.80717 2.61064 3.80717 3.14634 3.47675 3.47675C3.14634 3.80717 2.61064 3.80717 2.28023 3.47675C1.94983 3.14634 1.94983 2.61064 2.28023 2.28023ZM3.03481 2.72219C2.94848 2.63584 2.80852 2.63584 2.72219 2.72219C2.63584 2.80852 2.63584 2.94848 2.72219 3.03481C2.80852 3.12114 2.94848 3.12114 3.03481 3.03481C3.12114 2.94848 3.12114 2.80852 3.03481 2.72219Z" fill="#505050"/>
                </g>
                <defs>
                  <clipPath id="clip0_2486_1092_${item.key.replace(/[^a-zA-Z0-9]/g, '')}">
                    <rect width="10" height="10" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
              ${discountText}
            `;
            const insertAfter = priceRegular || priceSale || priceCompare;
            if (insertAfter && insertAfter.nextSibling) {
              priceContainer.insertBefore(discountBadge, insertAfter.nextSibling);
            } else {
              priceContainer.appendChild(discountBadge);
            }
          } else if (discountBadge) {
            const svg = discountBadge.querySelector('svg');
            if (svg) {
              discountBadge.innerHTML = svg.outerHTML + ' ' + discountText;
            } else {
              discountBadge.innerHTML = discountText;
            }
            discountBadge.style.display = 'inline';
          }
        }
        
        return; // Exit early - regular price shown, don't process sale price logic
      }
      
      // Compare price setting is enabled - show sale price and compare price as needed
      // Show sale price if we have discount OR if compare price exists
      const shouldShowSalePrice = hasDiscount || (hasComparePriceInAPI || (savedComparePrice && savedComparePrice > finalPrice));
      
      if (shouldShowSalePrice && (shouldShowComparePrice || hasDiscount)) {
        // Item has discount or compare price - show sale and compare prices
        if (!priceSale) {
          // Create sale price element
          const salePrice = document.createElement('span');
          salePrice.className = 'cart-new__price-sale';
          salePrice.textContent = this.formatMoney(finalPrice);
          if (priceRegular && priceRegular.parentNode) {
            priceContainer.insertBefore(salePrice, priceRegular);
          } else {
            priceContainer.appendChild(salePrice);
          }
        } else {
          priceSale.textContent = this.formatMoney(finalPrice);
          priceSale.style.display = 'inline';
        }
        
        // Show compare price only if setting is enabled and we have a value
        if (shouldShowComparePrice && comparePriceValue) {
          if (!priceCompare) {
            // Create compare price element
            const comparePriceEl = document.createElement('span');
            comparePriceEl.className = 'cart-new__price-compare';
            comparePriceEl.setAttribute('data-compare-price', comparePriceValue);
            comparePriceEl.setAttribute('data-price', comparePriceValue);
            comparePriceEl.textContent = this.formatMoney(comparePriceValue);
            if (priceSale && priceSale.nextSibling) {
              priceContainer.insertBefore(comparePriceEl, priceSale.nextSibling);
            } else {
              priceContainer.appendChild(comparePriceEl);
            }
          } else {
            priceCompare.textContent = this.formatMoney(comparePriceValue);
            priceCompare.setAttribute('data-compare-price', comparePriceValue);
            priceCompare.setAttribute('data-price', comparePriceValue);
            priceCompare.style.display = 'inline';
          }
        } else if (priceCompare) {
          // Hide compare price if it shouldn't be shown
          priceCompare.style.display = 'none';
        }
        
        // Also update row data attribute to preserve compare price
        if (comparePriceValue) {
          itemRow.setAttribute('data-compare-price', comparePriceValue);
        }
        
        if (priceRegular) {
          priceRegular.style.display = 'none';
        }
        
        // Update or create discount badge
        if (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) {
          const discountText = item.line_level_discount_allocations
            .map(d => `${d.discount_application.title} (-${this.formatMoney(d.amount)})`)
            .join(', ');
          
          if (!discountBadge && priceContainer) {
            // Create discount badge with SVG icon
            discountBadge = document.createElement('span');
            discountBadge.className = 'cart-new__discount-badge';
            discountBadge.innerHTML = `
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                <g clip-path="url(#clip0_2486_1092)">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M4.06792 0.313109C4.35156 0.303328 4.62653 0.411715 4.82722 0.6124L9.38759 5.17278C9.78747 5.57264 9.78747 6.22095 9.38759 6.62083L6.62083 9.38761C6.22095 9.78747 5.57264 9.78747 5.17278 9.38759L0.6124 4.82722C0.411715 4.62653 0.303328 4.35156 0.313109 4.06791L0.405336 1.39336C0.423879 0.855556 0.855559 0.423879 1.39336 0.405334L4.06792 0.313109ZM4.38528 1.05434C4.30709 0.976154 4.19997 0.933926 4.08945 0.937737L1.4149 1.02996C1.20537 1.03719 1.03719 1.20537 1.02996 1.4149L0.937737 4.08945C0.933926 4.19995 0.976154 4.30709 1.05434 4.38528L5.61472 8.94566C5.77051 9.10145 6.02309 9.10145 6.17887 8.94566L8.94566 6.17887C9.10145 6.02309 9.10145 5.77051 8.94566 5.61472L4.38528 1.05434ZM2.28023 2.28023C2.61064 1.94983 3.14634 1.94983 3.47675 2.28023C3.80717 2.61064 3.80717 3.14634 3.47675 3.47675C3.14634 3.80717 2.61064 3.80717 2.28023 3.47675C1.94983 3.14634 1.94983 2.61064 2.28023 2.28023ZM3.03481 2.72219C2.94848 2.63584 2.80852 2.63584 2.72219 2.72219C2.63584 2.80852 2.63584 2.94848 2.72219 3.03481C2.80852 3.12114 2.94848 3.12114 3.03481 3.03481C3.12114 2.94848 3.12114 2.80852 3.03481 2.72219Z" fill="#505050"/>
                </g>
                <defs>
                  <clipPath id="clip0_2486_1092_${item.key.replace(/[^a-zA-Z0-9]/g, '')}">
                    <rect width="10" height="10" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
              ${discountText}
            `;
            // Insert badge after price elements
            const insertAfter = priceCompare || priceSale || priceRegular;
            if (insertAfter && insertAfter.nextSibling) {
              priceContainer.insertBefore(discountBadge, insertAfter.nextSibling);
            } else {
              priceContainer.appendChild(discountBadge);
            }
          } else if (discountBadge) {
            // Update existing badge - preserve SVG, update text
            const svg = discountBadge.querySelector('svg');
            if (svg) {
              discountBadge.innerHTML = svg.outerHTML + ' ' + discountText;
            } else {
              discountBadge.innerHTML = discountText;
            }
            discountBadge.style.display = 'inline';
          }
        }
      } else {
        // No discount and no compare price - show regular price only
        if (priceCompare) {
          priceCompare.style.display = 'none';
        }
        if (priceSale) {
          priceSale.style.display = 'none';
        }
        if (discountBadge) {
          discountBadge.style.display = 'none';
        }
        
        // Always ensure regular price is visible
        if (!priceRegular) {
          // Create regular price element if it doesn't exist
          const regularPrice = document.createElement('span');
          regularPrice.className = 'cart-new__price-regular';
          regularPrice.textContent = this.formatMoney(finalPrice);
          // Remove any existing price elements first
          if (priceSale) priceSale.remove();
          if (priceCompare) priceCompare.remove();
          priceContainer.appendChild(regularPrice);
        } else {
          priceRegular.textContent = this.formatMoney(finalPrice);
          priceRegular.style.display = 'inline';
        }
        
        // Ensure at least one price is visible
        const visiblePrices = priceContainer.querySelectorAll('.cart-new__price-regular[style*="inline"], .cart-new__price-sale[style*="inline"]');
        if (visiblePrices.length === 0 && !priceContainer.querySelector('.cart-new__price-regular')) {
          // Fallback: create regular price if nothing is visible
          const fallbackPrice = document.createElement('span');
          fallbackPrice.className = 'cart-new__price-regular';
          fallbackPrice.textContent = this.formatMoney(finalPrice);
          priceContainer.appendChild(fallbackPrice);
        }
      }
    });

    // Remove items that are no longer in cart (only if we're sure they're gone)
    // Do this carefully - only remove if the cart data confirms the item is gone
    const existingRows = Array.from(this.querySelectorAll('.cart-new__item, tr[data-line-key]'));
    existingRows.forEach(row => {
      const lineKey = row.dataset.lineKey || row.getAttribute('data-line-key');
      if (lineKey && !existingLineKeys.has(lineKey)) {
        // Double-check: item should not exist in items array
        const itemStillExists = items.some(item => item.key === lineKey);
        if (!itemStillExists) {
          // Item is truly gone, remove it
          row.remove();
        }
      }
    });

    // Re-evaluate nesting group classes now that rows may have been removed
    this.syncChildGroupClasses();
  }

  updateCartTotals(cartData) {
    // Calculate total discount amount to determine if we should show subtotal
    let totalDiscount = 0;
    
    // Calculate line-level discounts
    if (cartData.items && cartData.items.length > 0) {
      cartData.items.forEach(item => {
        const originalLinePrice = item.original_line_price || item.final_line_price || 0;
        const finalLinePrice = item.final_line_price || 0;
        totalDiscount += (originalLinePrice - finalLinePrice);
      });
    }
    
    // Add cart-level discounts
    if (cartData.cart_level_discount_applications && cartData.cart_level_discount_applications.length > 0) {
      cartData.cart_level_discount_applications.forEach(discount => {
        totalDiscount += (discount.total_allocated_amount || 0);
      });
    }

    // Show or hide subtotal based on whether there are discounts
    const subtotalRow = this.querySelector('#cart-new-subtotal-row');
    if (subtotalRow) {
      if (totalDiscount > 0) {
        // Calculate and show original subtotal
        let originalSubtotal = 0;
        if (cartData.items && cartData.items.length > 0) {
          originalSubtotal = cartData.items.reduce((sum, item) => {
            return sum + (item.original_line_price || item.final_line_price || 0);
          }, 0);
        }
        
        const subtotalEl = this.querySelector('#cart-new-subtotal');
        if (subtotalEl) {
          subtotalEl.textContent = this.formatMoney(originalSubtotal);
        }
        subtotalRow.style.display = '';
      } else {
        subtotalRow.style.display = 'none';
      }
    }

    // Update discount rows
    this.updateDiscountRows(cartData);

    // Update total
    const totalEl = this.querySelector('#cart-new-total');
    if (totalEl) {
      totalEl.textContent = this.formatMoney(cartData.total_price);
    }
  }

  updateDiscountRows(cartData) {
    const totalsContainer = this.querySelector('.cart-new__totals');
    if (!totalsContainer) return;

    // Remove existing discount rows (except grand total)
    const existingDiscountRows = totalsContainer.querySelectorAll('.cart-new__total-row--discount');
    existingDiscountRows.forEach(row => row.remove());

    // Calculate total discount amount including:
    // 1. Line-level discounts (difference between original and final line prices)
    // 2. Cart-level discounts
    let totalDiscount = 0;
    
    // Calculate line-level discounts
    if (cartData.items && cartData.items.length > 0) {
      cartData.items.forEach(item => {
        const originalLinePrice = item.original_line_price || item.final_line_price || 0;
        const finalLinePrice = item.final_line_price || 0;
        totalDiscount += (originalLinePrice - finalLinePrice);
      });
    }
    
    // Add cart-level discounts
    if (cartData.cart_level_discount_applications && cartData.cart_level_discount_applications.length > 0) {
      cartData.cart_level_discount_applications.forEach(discount => {
        totalDiscount += (discount.total_allocated_amount || 0);
      });
    }

    // Show or hide discount row
    const grandTotalRow = totalsContainer.querySelector('.cart-new__total-row--grand');
    
    if (totalDiscount > 0) {
      // Create discount row if it doesn't exist or was removed
      const discountRow = document.createElement('div');
      discountRow.className = 'cart-new__total-row cart-new__total-row--discount';
      discountRow.innerHTML = `
        <span class="cart-new__total-label">Discount</span>
        <span class="cart-new__total-value cart-new__total-value--discount" id="cart-new-discount">-${this.formatMoney(totalDiscount)}</span>
      `;
      
      if (grandTotalRow) {
        totalsContainer.insertBefore(discountRow, grandTotalRow);
      } else {
        totalsContainer.appendChild(discountRow);
      }
    }
  }

  updateFreeShipping(cartData) {
    // Trigger free shipping bar update if it exists
    const freeShippingBar = document.querySelector('free-shipping-bar');
    if (freeShippingBar && typeof freeShippingBar.update === 'function') {
      freeShippingBar.update(cartData);
    }
    
    // Update free shipping bar in the sidebar if exists
    const freeShippingContainer = this.querySelector('#cart-new-free-shipping');
    if (freeShippingContainer && cartData.requires_shipping) {
      // Trigger update for free shipping component
      const freeShippingComponent = freeShippingContainer.querySelector('free-shipping-bar');
      if (freeShippingComponent) {
        // The component should listen to cart:change events
      }
    }
  }

  // The Affirm app block renders its "as low as $X/mo" estimate once at page
  // load from the data-amount it was served with; after an AJAX cart change
  // the amount must be rewritten and affirm.js asked to re-scan, or the
  // estimate stays frozen at the page-load total. No-ops if the block or the
  // Affirm script isn't on the page.
  updateAffirmMessaging(totalCents) {
    const affirmEls = document.querySelectorAll('.cart-new__affirm [data-amount]');
    if (!affirmEls.length) return;
    affirmEls.forEach(el => el.setAttribute('data-amount', totalCents));
    if (window.affirm && window.affirm.ui && typeof window.affirm.ui.refresh === 'function') {
      window.affirm.ui.refresh();
    }
  }

  // Mirrors the theme's formatMoney helper (theme.js.liquid — it's a module
  // export we can't import from a classic script) using the same
  // shop.money_format the server render uses, via window.themeVariables.
  // Every JS-written price therefore matches the Liquid `| money_without_trailing_zeros` output
  // exactly: same symbol, same thousands separators, no appended currency
  // code. The old helpers dropped separators and bolted on " USD".
  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    const format = (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat)
      || '${{amount_no_decimals}}';
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = match ? match[1] : 'amount';
    const withDelimiters = (number, precision, thousands, decimal) => {
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      // Drop an exact .00 so non-USD prices match Liquid's money_without_trailing_zeros
      return dollars + (parts[1] && parts[1] !== '00' ? decimal + parts[1] : '');
    };
    let value;
    switch (placeholder) {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', '.'); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' '); break;
      case 'amount_no_decimals_with_apostrophe_separator': value = withDelimiters(cents, 0, "'"); break;
      default: value = withDelimiters(cents, 2);
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }
}

// Register the custom element
if (!customElements.get('cart-new-section')) {
  customElements.define('cart-new-section', CartNew);
}

